import { useRouter } from "@tanstack/react-router";
import { AppType } from "#/unique/domain";
import { getBusinessAuthUserInfo } from "~/services/business/auth";
import { shouldDeferDefaultNavigationForIntent } from "&/navigationIntent";

export const useLoginHandlers = () => {
  const { history } = useRouter();

  const handleLoginSuccess = async (
    userType: AppType,
    userData: any,
    isAutoLogin: boolean = false
  ) => {
    console.log("[LOGIN-SUCCESS] 진입", { userType, hasUserData: !!userData, isAutoLogin });
    try {
      // 자동로그인이 아닌 경우에만 앱 환경에서 해시값을 앱에 전달
      if (!isAutoLogin) {
        const isIOS = /MT_I/i.test(navigator.userAgent);
        const isAndroid = /MT_A/i.test(navigator.userAgent);

        if ((isIOS || isAndroid) && window.mung?.appLogin && userData?.hash) {
          console.log("앱 환경에서 해시값 전송:", userData.hash);
          window.mung.appLogin(userData.hash);
          // 앱에서 callAppLogin을 호출할 때까지 대기
          // callAppLogin에서 메인 페이지로 이동하도록 처리됨
          return; // 여기서 함수 종료, 앱의 callAppLogin을 기다림
        }
      }

      // 자동로그인이거나 앱이 아닌 경우 직접 페이지 이동
      console.log("직접 페이지 이동 (자동로그인 또는 웹)", {
        isAutoLogin,
        userType,
      });

      if (shouldDeferDefaultNavigationForIntent()) {
        console.log("pending 딥링크 우선 처리로 기본 로그인 이동 보류");
        return;
      }

      if (userType === "business") {
        // 비즈니스 사용자 권한에 따른 리다이렉트
        const businessUserInfo = await getBusinessAuthUserInfo();
        if (businessUserInfo.isOwner) {
          history.push("/business/layout/home");
        } else if (
          businessUserInfo.isTeacher &&
          businessUserInfo.assignedCompany
        ) {
          history.push("/business/layout/home");
        } else {
          history.push("/business/layout/teacher");
        }
      } else {
        // 일반 고객
        history.push("/customer/layout/home");
      }
    } catch (error) {
      console.error("로그인 성공 후 처리 중 오류:", error);
      // 에러 발생 시 기본 페이지로 리다이렉트
      history.push("/");
    }
  };

  const handleLoginFailure = (
    userType: AppType,
    statusCode: number,
    snsType: string
  ) => {
    console.log("[LOGIN-FAILURE] 진입", { userType, statusCode, snsType });
    try {
      console.log("로그인 실패 처리:", { userType, statusCode, snsType });

      if (statusCode === 201) {
        // 회원가입이 필요한 경우 (신규 사용자)
        console.log(
          "회원가입이 필요한 사용자입니다. 회원가입 페이지로 이동합니다."
        );
        if (userType === "business") {
          history.push("/login-stage");
        } else {
          history.push("/sign-up");
        }
      } else {
        // 기타 실패 - 에러 메시지 표시
        console.log("로그인 실패:", statusCode);

        if (statusCode >= 500) {
          // 서버 오류
          console.log(
            `서버 오류가 발생했습니다. (${statusCode})\n잠시 후 다시 시도해주세요.`
          );
        } else if (statusCode === 401) {
          // 인증 실패
          console.log(
            `인증에 실패했습니다. (${statusCode})\nSNS 로그인을 다시 시도해주세요.`
          );
        } else if (statusCode === 403) {
          // 권한 없음
          console.log(
            `접근 권한이 없습니다. (${statusCode})\n관리자에게 문의해주세요.`
          );
        } else if (statusCode === 400) {
          // 잘못된 요청
          console.log(
            `잘못된 요청입니다. (${statusCode})\n입력 정보를 확인해주세요.`
          );
        } else if (statusCode === 404) {
          // 리소스 없음
          console.log(`요청한 리소스를 찾을 수 없습니다. (${statusCode})`);
        } else {
          // 기타 오류
          console.log(
            `로그인에 실패했습니다. (${statusCode})\n다시 시도해주세요.`
          );
        }
      }
    } catch (error) {
      console.error("로그인 실패 처리 중 오류:", error);
      // 에러 발생 시 기본 페이지로
      history.push("/");
    }
  };

  return { handleLoginSuccess, handleLoginFailure };
};
